import Phaser from 'phaser';
import { GAME, CARD, ZONES, HAND, ENEMY, UI, ANIM } from '../constants';
import { CombatManager } from '../combat/CombatManager';
import {
  createSlime, createGremlin, createFungus, createSkeleton,
  createMirrorGolem, createNumberLeech, createCrystalGuardian,
  createAbacus,
  createShade, createBerserker, createWraith,
  createClockworkKnight, createVoidEater,
  createEquation,
} from '../enemies/Enemy';
import { CardData, CardModifier, CardSlot, CombatResult, EnemyData, NodeType, StatusState } from '../types';
import { setGlobalSeed, getRng } from '../utils/Random';
import { RunData, createNewRun, hasRelic, normalizeRunData } from '../run/RunState';
import { generateVictoryRewards } from '../cards/ModifierGen';
import { detectCombos } from '../combat/ComboSystem';
import { getCurseById } from '../cards/CurseRegistry';
import { FONT_FAMILY, drawPixelBackdrop } from '../ui/PixelTheme';
import { fadeIn, fadeOut, shakeLight, shakeHeavy } from '../ui/SceneTransitions';
import { HealthBar } from '../ui/HealthBar';
import { createAmbientMotes, emitDamageParticles, emitHealParticles, emitBlockParticles, emitComboParticles } from '../ui/ParticleEffects';
import { showFloatingNumber, showComboPopup } from '../ui/CombatEffects';
import { drawEnemySprite } from '../ui/EnemySprites';
import { BOSS_INTROS } from '../story/Lore';

function getEffectDisplayName(type: string): string {
  switch (type) {
    case 'extra_damage': return 'Bonus Damage';
    case 'extra_block': return 'Bonus Block';
    case 'heal': return 'Heal';
    case 'buff_attack': return 'Attack Buff';
    case 'debuff_enemy': return 'Enemy Debuff';
    case 'draw': return 'Extra Draw';
    case 'draw_penalty': return 'Draw Penalty';
    case 'zone_cap_attack': return 'CAP+ATK';
    case 'zone_cap_defense': return 'CAP+DEF';
    case 'zone_cap_ability': return 'CAP+ABL';
    case 'zone_cap_all': return 'CAP+ALL';
    default: return type;
  }
}

interface CardSprite {
  container: Phaser.GameObjects.Container;
  bg: Phaser.GameObjects.Rectangle;
  numText: Phaser.GameObjects.Text;
  modTagText?: Phaser.GameObjects.Text;
  modStateText?: Phaser.GameObjects.Text;
  data: CardData;
  originalX: number;
  originalY: number;
  originalRotation: number;
  baseScale: number;
}

interface ZoneSprite {
  container: Phaser.GameObjects.Container;
  bg: Phaser.GameObjects.Rectangle;
  label: Phaser.GameObjects.Text;
  countText: Phaser.GameObjects.Text;
  descText: Phaser.GameObjects.Text;
  slot: Exclude<CardSlot, 'hand'>;
  cards: CardSprite[];
}

const LAYOUT = {
  arenaCenterX: 374,
  arenaWidth: 684,
  handRailWidth: 684,
  enemyX: 374,
  topBarY: 34,
  enemyY: 148,
  zoneY: 356,
  zoneX: [148, 374, 600] as const,
  rightPanelX: 852,
  rightPanelW: 278,
  logPanelY: 184,
  logPanelH: 126,
  tacticalPanelY: 330,
  tacticalPanelH: 144,
  targetPanelY: 452,
  targetPanelH: 104,
  forecastPanelY: 590,
  forecastPanelH: 108,
  handY: 604,
  comboY: 464,
  endTurnY: 500,
  feedbackY: 210,
} as const;

const THEME = {
  panelDark: 0x191a28,
  panelMid: 0x2a2032,
  panelStroke: 0xc3673f,
  textBright: '#f8f2e8',
  textMuted: '#c0b8aa',
  accentGold: '#ffd166',
  accentCyan: '#8de8dc',
  accentGreen: '#78d690',
  accentRed: '#ff8a72',
} as const;

export class CombatScene extends Phaser.Scene {
  private combat!: CombatManager;
  private cardSprites: CardSprite[] = [];
  private zones: ZoneSprite[] = [];
  private endTurnBtn!: Phaser.GameObjects.Text;
  private playerHpText!: Phaser.GameObjects.Text;
  private playerHpBar!: Phaser.GameObjects.Rectangle;
  private playerHpBg!: Phaser.GameObjects.Rectangle;
  private playerHealthBar!: HealthBar;
  private enemyHealthBar!: HealthBar;
  private deckCountText!: Phaser.GameObjects.Text;
  private enemyNameText!: Phaser.GameObjects.Text;
  private enemyHpText!: Phaser.GameObjects.Text;
  private enemyHpBar!: Phaser.GameObjects.Rectangle;
  private enemyHpBg!: Phaser.GameObjects.Rectangle;
  private enemyIntentText!: Phaser.GameObjects.Text;
  private enemySprite!: Phaser.GameObjects.Rectangle;
  private enemyStanceText!: Phaser.GameObjects.Text;
  private enemyStatusText!: Phaser.GameObjects.Text;
  private mutatorText!: Phaser.GameObjects.Text;
  private turnText!: Phaser.GameObjects.Text;
  private feedbackText!: Phaser.GameObjects.Text;
  private feedbackBg!: Phaser.GameObjects.Rectangle;
  private tooltip!: Phaser.GameObjects.Container;
  private tooltipBg!: Phaser.GameObjects.Rectangle;
  private tooltipText!: Phaser.GameObjects.Text;
  private abilityWarning!: Phaser.GameObjects.Text;
  private playerBlockText!: Phaser.GameObjects.Text;
  private playerBlockIcon!: Phaser.GameObjects.Rectangle;
  private playerStatusText!: Phaser.GameObjects.Text;
  private enemyBlockText!: Phaser.GameObjects.Text;
  private enemyBlockIcon!: Phaser.GameObjects.Rectangle;
  private resetTurnBtn!: Phaser.GameObjects.Text;
  private turnForecastText!: Phaser.GameObjects.Text;
  private turnForecastBg!: Phaser.GameObjects.Rectangle;
  private comboPreviewText!: Phaser.GameObjects.Text;
  private combatLogText!: Phaser.GameObjects.Text;
  private combatLogBg!: Phaser.GameObjects.Rectangle;
  private run!: RunData;
  private nodeType: NodeType = 'combat';
  private helpOverlay: Phaser.GameObjects.GameObject[] = [];
  private ambientObjects: Phaser.GameObjects.GameObject[] = [];
  private targetPanelObjects: Phaser.GameObjects.GameObject[] = [];
  private loadoutOverlay: Phaser.GameObjects.GameObject[] = [];
  private activeDropZone: ZoneSprite | null = null;

  constructor() {
    super({ key: 'CombatScene' });
  }

  create(data?: { run?: RunData; nodeType?: NodeType }): void {
    fadeIn(this);
    this.cardSprites = [];
    this.zones = [];
    this.helpOverlay = [];
    this.ambientObjects = [];
    this.targetPanelObjects = [];
    this.loadoutOverlay = [];
    this.activeDropZone = null;

    this.run = normalizeRunData(data?.run ?? createNewRun());
    this.nodeType = data?.nodeType ?? 'combat';
    this.run.battlesWon = this.run.battlesWon ?? 0;
    setGlobalSeed(this.run.seed + this.run.visitedNodeIds.length);

    const enemy = this.pickEnemy();

    this.combat = new CombatManager(
      enemy,
      this.run.deckNumbers,
      this.run.playerHp,
      this.run.playerMaxHp,
      this.run.modifiers,
      this.run.permanentExhaust,
      this.run.relics,
      this.run.abilities,
      this.run.curses,
    );

    // Apply run-level draw bonus from Ancient Library event
    if (this.run.drawBonusBattles > 0) {
      this.combat.player.buffs.push({ type: 'draw_bonus', value: 1, duration: 99 });
    }

    this.createBackdrop();
    this.createZones();
    this.createEnemyDisplay();
    this.createPlayerHUD();
    this.createCombatLogPanel();
    this.createEndTurnButton();
    this.createResetButton();
    this.createTurnForecastPanel();
    this.createComboPreview();
    this.createFeedbackText();
    this.createTooltip();
    this.createAbilityWarning();
    this.createHelpButton();
    this.createLoadoutButton();

    this.input.on('drag', (_pointer: Phaser.Input.Pointer, obj: Phaser.GameObjects.Container, dragX: number, dragY: number) => {
      obj.x = dragX;
      obj.y = dragY;
      this.hideTooltip();
      this.updateDropZoneHighlight(dragX, dragY);
    });

    this.input.on('dragstart', (_pointer: Phaser.Input.Pointer, obj: Phaser.GameObjects.Container) => {
      obj.setDepth(50);
      this.updateDropZoneHighlight(obj.x, obj.y);
    });

    this.input.on('dragend', (_pointer: Phaser.Input.Pointer, obj: Phaser.GameObjects.Container) => {
      const cardSprite = this.cardSprites.find(cs => cs.container === obj);
      if (!cardSprite) return;
      this.handleCardDrop(cardSprite);
      obj.setDepth(0);
      this.resetDropZoneHighlight();
    });

    this.input.keyboard?.on('keydown-SPACE', () => {
      if (this.combat.canEndTurn() && this.combat.phase === 'assign') {
        this.resolveEndTurn();
      }
    });
    this.input.keyboard?.on('keydown-ENTER', () => {
      if (this.combat.canEndTurn() && this.combat.phase === 'assign') {
        this.resolveEndTurn();
      }
    });
    this.input.keyboard?.on('keydown-R', () => {
      if (this.combat.phase === 'assign') {
        this.resetAssignments(true);
      }
    });
    this.input.keyboard?.on('keydown-H', () => {
      this.toggleHelp();
    });
    this.input.keyboard?.on('keydown-TAB', (event: KeyboardEvent) => {
      event.preventDefault();
      this.toggleLoadoutOverlay();
    });

    this.startTurn();

    if (this.nodeType === 'boss') {
      this.showBossIntro();
    }
  }

  // Dramatic curtain shown over the arena before a boss fight. The combat is
  // already set up underneath; dismissing the curtain just reveals the board.
  private showBossIntro(): void {
    const cx = GAME.WIDTH / 2;
    const cy = GAME.HEIGHT / 2;
    const intro = this.run.act >= 2 ? BOSS_INTROS.equation : BOSS_INTROS.abacus;
    const D = 9000;
    const layer: Phaser.GameObjects.GameObject[] = [];

    const curtain = this.add.rectangle(cx, cy, GAME.WIDTH, GAME.HEIGHT, 0x05060c, 0.92)
      .setDepth(D).setInteractive();
    const panel = this.add.rectangle(cx, cy, GAME.WIDTH - 260, 300, 0x140d18, 0.97)
      .setStrokeStyle(3, 0xc0334a, 0.95).setDepth(D + 1);
    const accentTop = this.add.rectangle(cx, cy - 146, GAME.WIDTH - 282, 3, 0xff5a6e, 0.85).setDepth(D + 1);
    const accentBot = this.add.rectangle(cx, cy + 146, GAME.WIDTH - 282, 3, 0xff5a6e, 0.5).setDepth(D + 1);

    const warn = this.add.text(cx, cy - 104, '!  LEDGER GUARDIAN  !', {
      fontSize: '11px', fontFamily: FONT_FAMILY, color: '#ff8a72', fontStyle: 'bold',
    }).setOrigin(0.5).setDepth(D + 2);
    const name = this.add.text(cx, cy - 66, intro.name, {
      fontSize: '30px', fontFamily: FONT_FAMILY, color: '#ffd0d6', fontStyle: 'bold',
    }).setOrigin(0.5).setDepth(D + 2);
    const epithet = this.add.text(cx, cy - 32, intro.epithet, {
      fontSize: '12px', fontFamily: FONT_FAMILY, color: '#c79ad0',
    }).setOrigin(0.5).setDepth(D + 2);
    const line = this.add.text(cx, cy + 26, `"${intro.line}"`, {
      fontSize: '15px', fontFamily: FONT_FAMILY, color: '#eef0ff', fontStyle: 'italic',
      align: 'center', wordWrap: { width: GAME.WIDTH - 360 }, lineSpacing: 8,
    }).setOrigin(0.5).setDepth(D + 2);
    const prompt = this.add.text(cx, cy + 112, '[ BEGIN  SPACE ]', {
      fontSize: '13px', fontFamily: FONT_FAMILY, color: '#fff6dc',
      backgroundColor: '#3a1620', padding: { x: 18, y: 9 },
    }).setOrigin(0.5).setDepth(D + 2);
    prompt.setStroke('#0c0a16', 4);

    layer.push(curtain, panel, accentTop, accentBot, warn, name, epithet, line, prompt);

    this.tweens.add({ targets: prompt, alpha: 0.7, duration: 800, yoyo: true, repeat: -1, ease: 'Sine.easeInOut' });
    shakeHeavy(this);

    let dismissed = false;
    const dismiss = () => {
      if (dismissed) return;
      dismissed = true;
      this.tweens.add({
        targets: layer,
        alpha: 0,
        duration: 300,
        ease: 'Sine.easeOut',
        onComplete: () => layer.forEach(el => el.destroy()),
      });
    };
    curtain.on('pointerdown', dismiss);
    this.input.keyboard?.once('keydown-SPACE', dismiss);
    this.input.keyboard?.once('keydown-ENTER', dismiss);
  }

  private pickEnemy(): EnemyData {
    const rng = getRng();
    const act = this.run.act;

    if (this.nodeType === 'boss') {
      return act >= 2 ? createEquation() : createAbacus();
    }

    if (this.nodeType === 'elite') {
      if (act >= 2) {
        return rng.pick([createClockworkKnight, createVoidEater, createCrystalGuardian])();
      }
      return rng.pick([createMirrorGolem, createNumberLeech, createCrystalGuardian])();
    }

    const visited = this.run.visitedNodeIds.length;

    if (act >= 2) {
      const enemy = rng.pick([createShade, createBerserker, createWraith, createFungus, createSkeleton])();
      // Act 2 scaling
      const scale = 1 + visited * 0.05;
      enemy.maxHp = Math.round(enemy.maxHp * scale);
      enemy.hp = enemy.maxHp;
      return enemy;
    }

    // Act 1
    if (visited <= 2) return rng.pick([createSlime, createGremlin])();

    const enemy = rng.pick([createSlime, createGremlin, createFungus, createSkeleton])();
    if (visited >= 4) {
      const scale = 1 + (visited - 3) * 0.1;
      enemy.maxHp = Math.round(enemy.maxHp * scale);
      enemy.hp = enemy.maxHp;
    }
    return enemy;
  }

  private createBackdrop(): void {
    const cx = GAME.WIDTH / 2;
    const cy = GAME.HEIGHT / 2;
    drawPixelBackdrop(this);

    const frame = this.add.rectangle(cx, cy, GAME.WIDTH - 26, GAME.HEIGHT - 22, 0x000000, 0)
      .setStrokeStyle(3, THEME.panelStroke, 0.86)
      .setDepth(-25);
    const topHud = this.add.rectangle(cx, 34, GAME.WIDTH - 30, 44, 0x1a1d2f, 0.94)
      .setStrokeStyle(1, 0xcf7a54, 0.82)
      .setDepth(-24);
    const battlefield = this.add.rectangle(LAYOUT.arenaCenterX, 350, LAYOUT.arenaWidth + 56, 430, 0x141a2d, 0.84)
      .setStrokeStyle(1, 0x4e6487, 0.6)
      .setDepth(-24);
    const sidebar = this.add.rectangle(LAYOUT.rightPanelX, 380, LAYOUT.rightPanelW + 16, 520, 0x161a2b, 0.86)
      .setStrokeStyle(1, 0xbf6f4d, 0.7)
      .setDepth(-24);
    const bottomHud = this.add.rectangle(cx, GAME.HEIGHT - 58, GAME.WIDTH - 30, 106, 0x14182a, 0.94)
      .setStrokeStyle(1, 0x58739e, 0.64)
      .setDepth(-24);
    const arenaGlow = this.add.circle(LAYOUT.arenaCenterX, 344, 232, 0x88a6d4, 0.05).setDepth(-23);
    this.tweens.add({
      targets: arenaGlow,
      alpha: 0.1,
      duration: 2200,
      yoyo: true,
      repeat: -1,
      ease: 'Sine.easeInOut',
    });
    this.ambientObjects.push(frame, topHud, battlefield, sidebar, bottomHud, arenaGlow);
    const motes = createAmbientMotes(this, 8);
    this.ambientObjects.push(...motes);
  }

  // --- Tooltip ---

  private createTooltip(): void {
    this.tooltipBg = this.add.rectangle(0, 0, 240, 100, 0x111122, 0.95)
      .setStrokeStyle(1, 0x666688);
    this.tooltipText = this.add.text(0, 0, '', {
      fontSize: '12px',
      fontFamily: FONT_FAMILY,
      color: '#ddddee',
      lineSpacing: 2,
    }).setOrigin(0.5);
    this.tooltip = this.add.container(0, 0, [this.tooltipBg, this.tooltipText]);
    this.tooltip.setDepth(100);
    this.tooltip.setVisible(false);
  }

  private showTooltip(card: CardData, x: number, y: number): void {
    const lines: string[] = [];
    if (card.kind === 'curse') {
      const curse = card.curseId ? getCurseById(card.curseId) : undefined;
      lines.push(`--- ${card.curseName ?? 'CURSE'} ---`);
      lines.push(`CURSE card (base value 0)`);
      lines.push('');
      lines.push('On Draw:');
      if (curse?.onDraw?.hpLoss) {
        lines.push(`- Lose ${curse.onDraw.hpLoss} HP`);
      }
      if (curse?.onDraw?.drawPenaltyNextTurn) {
        lines.push(`- Draw -${curse.onDraw.drawPenaltyNextTurn} next turn`);
      }
      if (!curse?.onDraw) {
        lines.push('- none');
      }

      lines.push('');
      lines.push('On Play:');
      if (curse?.onPlay) {
        const slot = this.getTriggerLabel(curse.onPlay.slot);
        const effects: string[] = [];
        if (curse.onPlay.hpLoss) effects.push(`lose ${curse.onPlay.hpLoss} HP`);
        if (curse.onPlay.drawPenaltyNextTurn) effects.push(`draw -${curse.onPlay.drawPenaltyNextTurn} next turn`);
        lines.push(`- In ${slot}: ${effects.join(', ')}`);
      } else {
        lines.push('- none');
      }

      lines.push('');
      lines.push(`Active In Zone: ${card.slot === 'hand' ? 'HAND' : this.getTriggerLabel(card.slot)}`);
      if (card.curseText) {
        lines.push('');
        lines.push(card.curseText);
      }
    } else {
      const n = card.number;
      const isNeg = n < 0;
      const absN = Math.abs(n);
      lines.push(`--- Card ${n} ---`);

      if (isNeg) {
        lines.push(`ATK: Heals enemy ${absN}`);
        lines.push(`DEF: Weakens your block by ${absN}`);
        lines.push(`ABL: No base effect`);
        lines.push(``);
        lines.push(`Negative = inverse ATK/DEF`);
        lines.push(`But modifiers still apply!`);
      } else {
        lines.push(`ATK: Deal ${n} damage`);
        lines.push(`DEF: Block ${n} damage`);
        lines.push(`ABL: No base effect`);
      }
    }

    if (card.modifiers.length > 0) {
      lines.push(``);
      lines.push(`Active In Zone:`);
      for (const mod of card.modifiers) {
        const effectName = getEffectDisplayName(mod.effect.type);
        const triggerName = mod.trigger === 'attack' ? 'ATK'
          : mod.trigger === 'defense' ? 'DEF' : 'ABL';
        const tempTag = mod.temporary ? ' [TEMP]' : '';
        const nameTag = mod.name ? `${mod.name}: ` : '';
        const zoneStatus = card.slot !== 'hand'
          ? (card.slot === mod.trigger ? 'ACTIVE' : `OFF (${this.getTriggerLabel(card.slot)})`)
          : 'READY';
        lines.push(`${nameTag}[${triggerName}] ${effectName} +${mod.effect.value}${tempTag} - ${zoneStatus}`);
      }
    }

    const text = lines.join('\n');
    this.tooltipText.setText(text);

    const bounds = this.tooltipText.getBounds();
    this.tooltipBg.setSize(bounds.width + 24, bounds.height + 16);

    const tooltipY = Math.max(60, y - 120);
    const tooltipX = Phaser.Math.Clamp(x, 130, GAME.WIDTH - 130);
    this.tooltip.setPosition(tooltipX, tooltipY);
    this.tooltip.setVisible(true);
  }

  private hideTooltip(): void {
    this.tooltip.setVisible(false);
  }

  private getTriggerLabel(slot: CardSlot): string {
    if (slot === 'attack') return 'ATK';
    if (slot === 'defense') return 'DEF';
    if (slot === 'ability') return 'ABL';
    return 'HAND';
  }

  private getCardModifierTargets(card: CardData): CardSlot[] {
    const ordered: CardSlot[] = ['attack', 'defense', 'ability'];
    return ordered.filter(slot => card.modifiers.some(mod => mod.trigger === slot));
  }

  private getCardMatchingModifiers(card: CardData): number {
    if (card.slot === 'hand') return 0;
    return card.modifiers.filter(mod => mod.trigger === card.slot).length;
  }

  private updateCardModifierIndicator(cardSprite: CardSprite): void {
    const card = cardSprite.data;
    if (!cardSprite.modTagText && !cardSprite.modStateText) return;

    if (card.modifiers.length === 0) {
      cardSprite.modTagText?.setVisible(false);
      cardSprite.modStateText?.setVisible(false);
      return;
    }

    const targets = this.getCardModifierTargets(card);
    const targetLabel = targets.map(slot => this.getTriggerLabel(slot)).join('/');
    const badges = Array.from(new Set(card.modifiers.map(mod => this.getModifierBadge(mod.effect.type)))).join(' ');
    const matching = this.getCardMatchingModifiers(card);
    const isHand = card.slot === 'hand';

    cardSprite.modTagText?.setVisible(true);
    cardSprite.modTagText?.setText(`${badges || 'MOD'} ${targetLabel}`);

    if (isHand) {
      cardSprite.modTagText?.setColor('#ffd166');
      cardSprite.modStateText?.setVisible(true);
      cardSprite.modStateText?.setText('Ready');
      cardSprite.modStateText?.setColor('#99ccaa');
      cardSprite.bg.setStrokeStyle(2, 0xffd166);
      return;
    }

    if (matching > 0) {
      cardSprite.modTagText?.setColor('#66dd88');
      cardSprite.modStateText?.setVisible(true);
      cardSprite.modStateText?.setText('ACTIVE');
      cardSprite.modStateText?.setColor('#66dd88');
      cardSprite.bg.setStrokeStyle(2, this.getSlotBorderColor(card.slot));
    } else {
      cardSprite.modTagText?.setColor('#ff9999');
      cardSprite.modStateText?.setVisible(true);
      cardSprite.modStateText?.setText(`OFF (need ${targetLabel})`);
      cardSprite.modStateText?.setColor('#ff8888');
      cardSprite.bg.setStrokeStyle(2, 0xff5555);
    }
  }

  private getModifierBadge(effectType: string): string {
    switch (effectType) {
      case 'zone_cap_attack': return 'CAP+ATK';
      case 'zone_cap_defense': return 'CAP+DEF';
      case 'zone_cap_ability': return 'CAP+ABL';
      case 'zone_cap_all': return 'CAP+ALL';
      case 'draw': return 'DRAW+';
      case 'draw_penalty': return 'DRAW-';
      default: return 'MOD';
    }
  }

  private getZoneAtPosition(x: number, y: number): ZoneSprite | null {
    for (const zone of this.zones) {
      const zoneBounds = new Phaser.Geom.Rectangle(
        zone.container.x - ZONES.WIDTH / 2,
        zone.container.y - ZONES.HEIGHT / 2,
        ZONES.WIDTH,
        ZONES.HEIGHT,
      );
      if (zoneBounds.contains(x, y)) return zone;
    }
    return null;
  }

  private updateDropZoneHighlight(x: number, y: number): void {
    const hovered = this.getZoneAtPosition(x, y);
    if (hovered === this.activeDropZone) return;
    this.activeDropZone = hovered;

    for (const zone of this.zones) {
      const isActive = zone === hovered;
      const baseColor = zone.slot === 'attack'
        ? ZONES.ATTACK.color
        : zone.slot === 'defense'
          ? ZONES.DEFENSE.color
          : ZONES.ABILITY.color;
      zone.bg.setStrokeStyle(isActive ? 3 : 2, baseColor, isActive ? 0.95 : 0.62);
      zone.bg.setFillStyle(baseColor, isActive ? 0.16 : 0.1);
      zone.container.setScale(isActive ? 1.02 : 1);
    }
  }

  private resetDropZoneHighlight(): void {
    this.activeDropZone = null;
    for (const zone of this.zones) {
      const baseColor = zone.slot === 'attack'
        ? ZONES.ATTACK.color
        : zone.slot === 'defense'
          ? ZONES.DEFENSE.color
          : ZONES.ABILITY.color;
      zone.bg.setStrokeStyle(2, baseColor, 0.62);
      zone.bg.setFillStyle(baseColor, 0.1);
      zone.container.setScale(1);
    }
  }

  // --- Zones ---

  private createAbilityWarning(): void {
    const abilityZone = this.zones.find(z => z.slot === 'ability');
    if (!abilityZone) return;
    this.abilityWarning = this.add.text(
      abilityZone.container.x,
      abilityZone.container.y + ZONES.HEIGHT / 2 + 22,
      '',
      {
        fontSize: '10px',
        fontFamily: FONT_FAMILY,
        color: '#d2c597',
        backgroundColor: '#11182f',
        padding: { x: 5, y: 2 },
      }
    ).setOrigin(0.5).setDepth(5);
  }

  private createZones(): void {
    const zoneDefs: { x: number; y: number; label: string; color: number; slot: Exclude<CardSlot, 'hand'>; desc: string }[] = [
      { x: LAYOUT.zoneX[0], y: LAYOUT.zoneY, label: 'ATTACK', color: ZONES.ATTACK.color, slot: 'attack', desc: 'Deal damage' },
      { x: LAYOUT.zoneX[1], y: LAYOUT.zoneY, label: 'DEFENSE', color: ZONES.DEFENSE.color, slot: 'defense', desc: 'Gain block' },
      { x: LAYOUT.zoneX[2], y: LAYOUT.zoneY, label: 'ABILITY', color: ZONES.ABILITY.color, slot: 'ability', desc: 'Trigger modifiers' },
    ];

    for (const def of zoneDefs) {
      const shadow = this.add.rectangle(0, 6, ZONES.WIDTH + 8, ZONES.HEIGHT + 8, 0x03050b, 0.3);
      const bg = this.add.rectangle(0, 0, ZONES.WIDTH, ZONES.HEIGHT, def.color, 0.1)
        .setStrokeStyle(2, def.color, 0.62);
      const header = this.add.rectangle(0, -ZONES.HEIGHT / 2 + 16, ZONES.WIDTH - 10, 30, def.color, 0.2)
        .setStrokeStyle(1, def.color, 0.48);
      const inner = this.add.rectangle(0, 14, ZONES.WIDTH - 16, ZONES.HEIGHT - 78, 0x0e1430, 0.56)
        .setStrokeStyle(1, def.color, 0.26);
      const headerGlow = this.add.rectangle(0, -ZONES.HEIGHT / 2 + 4, ZONES.WIDTH - 22, 6, def.color, 0.22);
      const footer = this.add.rectangle(0, ZONES.HEIGHT / 2 + 16, ZONES.WIDTH - 10, 24, 0x11172a, 0.9)
        .setStrokeStyle(1, def.color, 0.35);

      const label = this.add.text(0, -ZONES.HEIGHT / 2 + 8, def.label, {
        fontSize: '10px',
        fontFamily: FONT_FAMILY,
        color: THEME.textBright,
        fontStyle: 'bold',
      }).setOrigin(0.5, 0);

      // Zone description - always visible below label
      const descText = this.add.text(0, -ZONES.HEIGHT / 2 + 28, def.desc, {
        fontSize: '10px',
        fontFamily: FONT_FAMILY,
        color: THEME.textMuted,
        align: 'center',
        wordWrap: { width: ZONES.WIDTH - 20 },
      }).setOrigin(0.5, 0);

      const countText = this.add.text(0, ZONES.HEIGHT / 2 + 16, '', {
        fontSize: '10px',
        fontFamily: FONT_FAMILY,
        color: THEME.textBright,
        align: 'center',
        wordWrap: { width: ZONES.WIDTH - 18 },
      }).setOrigin(0.5);

      const container = this.add.container(def.x, def.y, [shadow, bg, inner, header, headerGlow, footer, label, descText, countText]);
      this.zones.push({ container, bg, label, countText, descText, slot: def.slot, cards: [] });
    }
  }

  // --- Combo Preview ---

  private createComboPreview(): void {
    this.comboPreviewText = this.add.text(LAYOUT.arenaCenterX, LAYOUT.comboY, '', {
      fontSize: '9px',
      fontFamily: FONT_FAMILY,
      color: '#ffd166',
      fontStyle: 'bold',
      align: 'center',
    }).setOrigin(0.5).setDepth(5);
  }

  private updateComboPreview(): void {
    if (this.combat.phase !== 'assign') {
      this.comboPreviewText.setText('');
      return;
    }

    const assignment = this.combat.getAssignment();
    const allCards = [...assignment.attack, ...assignment.defense, ...assignment.ability];
    if (allCards.length === 0) {
      this.comboPreviewText.setText('');
      return;
    }

    const combos = detectCombos(assignment, this.run.relics);
    if (combos.length > 0) {
      const names = combos.map(c => c.name).join(' + ');
      this.comboPreviewText.setText(`COMBO: ${names}`);
      this.comboPreviewText.setColor('#ffd700');
    } else {
      this.comboPreviewText.setText('No combo');
      this.comboPreviewText.setColor('#444466');
    }
  }

  // --- Help Panel ---

  private createHelpButton(): void {
    const btn = this.add.text(GAME.WIDTH - 44, LAYOUT.topBarY, '[ ? ]', {
      fontSize: '10px',
      fontFamily: FONT_FAMILY,
      color: '#9ab0e3',
      backgroundColor: '#1a2548',
      padding: { x: 6, y: 4 },
    }).setOrigin(0.5).setInteractive({ useHandCursor: true }).setDepth(5);
    btn.setStroke('#0a0f20', 3);

    btn.on('pointerover', () => btn.setColor(THEME.accentGold));
    btn.on('pointerout', () => btn.setColor('#9ab0e3'));
    btn.on('pointerdown', () => this.toggleHelp());
  }

  private createLoadoutButton(): void {
    const btn = this.add.text(130, LAYOUT.topBarY, '[ LOADOUT ]', {
      fontSize: '12px',
      fontFamily: FONT_FAMILY,
      color: '#a5c4ff',
      backgroundColor: '#18253e',
      padding: { x: 8, y: 3 },
    }).setOrigin(0.5).setInteractive({ useHandCursor: true }).setDepth(5);
    btn.setStroke('#091124', 2);

    btn.on('pointerover', () => btn.setColor('#e5f0ff'));
    btn.on('pointerout', () => btn.setColor('#a5c4ff'));
    btn.on('pointerdown', () => this.toggleLoadoutOverlay());
  }

  private getShortModifierLabel(mod: CardModifier): string {
    const trigger = mod.trigger === 'attack' ? 'A' : mod.trigger === 'defense' ? 'D' : 'L';
    const effect = mod.effect.type === 'extra_damage'
      ? 'dmg'
      : mod.effect.type === 'extra_block'
        ? 'blk'
        : mod.effect.type === 'heal'
          ? 'heal'
          : mod.effect.type === 'buff_attack'
            ? 'atk'
            : mod.effect.type === 'debuff_enemy'
              ? 'debuf'
              : mod.effect.type === 'draw'
                ? 'draw+'
                : mod.effect.type === 'draw_penalty'
                  ? 'draw-'
                  : mod.effect.type === 'zone_cap_attack'
                    ? 'capA'
                    : mod.effect.type === 'zone_cap_defense'
                      ? 'capD'
                      : mod.effect.type === 'zone_cap_ability'
                        ? 'capL'
                        : 'cap+';
    return `[${trigger}:${effect}+${mod.effect.value}]`;
  }

  private formatDeckForLoadout(): string {
    const counts = new Map<number, number>();
    for (const num of this.run.deckNumbers) {
      counts.set(num, (counts.get(num) ?? 0) + 1);
    }
    const sortedEntries = Array.from(counts.entries()).sort((a, b) => a[0] - b[0]);
    return sortedEntries.map(([num, count]) => {
      const mod = this.run.modifiers.get(num);
      const countLabel = count > 1 ? `x${count}` : '';
      const modLabel = mod ? this.getShortModifierLabel(mod) : '';
      return `${num}${countLabel}${modLabel}`;
    }).join('  ');
  }

  private toggleLoadoutOverlay(): void {
    if (this.loadoutOverlay.length > 0) {
      for (const obj of this.loadoutOverlay) obj.destroy();
      this.loadoutOverlay = [];
      return;
    }
    if (this.helpOverlay.length > 0) {
      this.toggleHelp();
    }

    const cx = GAME.WIDTH / 2;
    const panelShadow = this.add.rectangle(cx + 6, GAME.HEIGHT / 2 + 6, GAME.WIDTH - 152, GAME.HEIGHT - 170, 0x000000, 0.55)
      .setDepth(190);
    const panel = this.add.rectangle(cx, GAME.HEIGHT / 2, GAME.WIDTH - 152, GAME.HEIGHT - 170, 0x111728, 0.96)
      .setStrokeStyle(2, 0xcf7343, 0.95)
      .setDepth(191)
      .setInteractive();
    const inner = this.add.rectangle(cx, GAME.HEIGHT / 2, GAME.WIDTH - 166, GAME.HEIGHT - 186, 0x0a1122, 0.42)
      .setStrokeStyle(1, 0x5b7aa4, 0.7)
      .setDepth(191);

    const title = this.add.text(cx, 110, 'RUN LOADOUT', {
      fontSize: '14px',
      fontFamily: FONT_FAMILY,
      color: '#ffe4aa',
      fontStyle: 'bold',
    }).setOrigin(0.5).setDepth(192);

    const header = this.add.text(cx, 140,
      `Act ${this.run.act}  |  HP ${this.combat.player.hp}/${this.combat.player.maxHp}  |  Turn ${this.combat.turnNumber}  |  Deck ${this.run.deckNumbers.length}  |  Curses ${this.run.curses.length}`,
      {
        fontSize: '11px',
        fontFamily: FONT_FAMILY,
        color: '#c8d5f2',
      },
    ).setOrigin(0.5).setDepth(192);

    const relicText = this.run.relics.length > 0
      ? this.run.relics.map(relic => relic.name).join('  |  ')
      : 'none';
    const abilityText = this.run.abilities.length > 0
      ? this.run.abilities.map(ability => ability.name).join('  |  ')
      : 'none';
    const curseText = this.run.curses.length > 0
      ? this.run.curses.map(curseId => getCurseById(curseId)?.name ?? curseId).join(' | ')
      : 'none';

    const sections: Array<{ title: string; text: string; y: number; color: string }> = [
      { title: `RELICS (${this.run.relics.length})`, text: relicText, y: 184, color: '#ffd9a5' },
      { title: `ABILITIES (${this.run.abilities.length})`, text: abilityText, y: 268, color: '#a4f5dd' },
      { title: `CURSES (${this.run.curses.length})`, text: curseText, y: 352, color: '#ff99ad' },
      { title: `DECK (${this.run.deckNumbers.length})`, text: this.formatDeckForLoadout(), y: 436, color: '#c9d4ff' },
    ];

    const sectionObjects: Phaser.GameObjects.GameObject[] = [];
    for (const section of sections) {
      const heading = this.add.text(140, section.y, section.title, {
        fontSize: '12px',
        fontFamily: FONT_FAMILY,
        color: section.color,
        fontStyle: 'bold',
      }).setDepth(192);
      const body = this.add.text(140, section.y + 18, section.text, {
        fontSize: '10px',
        fontFamily: FONT_FAMILY,
        color: '#e7edf9',
        wordWrap: { width: GAME.WIDTH - 280 },
        lineSpacing: 4,
      }).setDepth(192);
      sectionObjects.push(heading, body);
    }

    const hint = this.add.text(cx, GAME.HEIGHT - 130, 'Tip: TAB toggles this panel during combat', {
      fontSize: '10px',
      fontFamily: FONT_FAMILY,
      color: '#9fb4df',
    }).setOrigin(0.5).setDepth(192);

    const closeBtn = this.add.text(cx, GAME.HEIGHT - 96, '[ CLOSE ]', {
      fontSize: '11px',
      fontFamily: FONT_FAMILY,
      color: '#ffffff',
      backgroundColor: '#2c3554',
      padding: { x: 16, y: 8 },
    }).setOrigin(0.5).setDepth(193).setInteractive({ useHandCursor: true });
    closeBtn.setStroke('#0a1124', 3);
    closeBtn.on('pointerdown', () => this.toggleLoadoutOverlay());

    this.loadoutOverlay = [panelShadow, panel, inner, title, header, hint, closeBtn, ...sectionObjects];
  }

  private toggleHelp(): void {
    if (this.helpOverlay.length > 0) {
      for (const obj of this.helpOverlay) obj.destroy();
      this.helpOverlay = [];
      return;
    }
    if (this.loadoutOverlay.length > 0) {
      this.toggleLoadoutOverlay();
    }

    const cx = GAME.WIDTH / 2;
    const cy = GAME.HEIGHT / 2;

    const bg = this.add.rectangle(cx, cy, 680, 660, 0x111122, 0.96)
      .setStrokeStyle(2, 0x444466).setDepth(200).setInteractive();

    const helpLines = [
      '=== ZONES ===',
      'ATTACK: card value = damage',
      'DEFENSE: card value = block',
      'ABILITY: only modifiers trigger',
      'Caps start at 1/1/1, buffs raise next turn caps',
      '',
      '=== CORE CONTROLS ===',
      'SPACE / ENTER: end turn',
      'R: reset all placed cards',
      'H: toggle this help panel',
      'TAB: open run loadout',
      '',
      '=== STANCES ===',
      'AGGRO: enemy attack up, takes more damage',
      'GUARD: enemy block + reduced incoming damage',
      'CAST: utility/debuff turn',
      '',
      '=== STATUSES ===',
      'FRAIL / EXPOSED / JAMMED on player',
      'BRITTLE / WEAKENED / WARDED on enemies',
      '',
      '=== CARDS ===',
      'Negative cards invert ATK/DEF outcomes',
      'Curses are value 0 with on-draw/on-play penalties',
      '',
      '=== NEGATIVE CARDS ===',
      'ATK: Heals enemy instead of damaging',
      'DEF: Reduces your block instead of adding',
      'ABL: no base effect (modifiers still work)',
      '',
      '=== COMBOS ===',
      'PERFECT 10: ATK + DEF = 10  (+3 dmg, +3 blk)',
      'ALL IN: All 3 zones filled  (+2 dmg, +2 blk)',
      'PARITY SHIFT / PRIME SET / POLAR SPIKE',
      'BARRAGE / BASTION / ARCANE NET',
      'STRAIGHT / TRINITY PRIME / DARK CURRENT',
      '',
      '=== DEPTH ===',
      'Mutators, stances, statuses, and curses stack together.',
      'Elites and bosses grant premium rewards.',
      'Use TAB for full run loadout details.',
    ];

    // Show active relics in help panel
    if (this.run.relics.length > 0) {
      helpLines.push('');
      helpLines.push('=== RELICS ===');
      for (const relic of this.run.relics) {
        helpLines.push(`${relic.name}: ${relic.description}`);
      }
    }

    if (this.run.abilities.length > 0) {
      helpLines.push('');
      helpLines.push('=== ABILITIES ===');
      for (const ability of this.run.abilities) {
        helpLines.push(`${ability.name}: ${ability.description}`);
      }
    }

    const helpText = helpLines.join('\n');

    const text = this.add.text(cx, cy - 10, helpText, {
      fontSize: '10px',
      fontFamily: FONT_FAMILY,
      color: '#ccccdd',
      lineSpacing: 2,
      align: 'left',
      wordWrap: { width: 630 },
    }).setOrigin(0.5).setDepth(201);

    const closeBtn = this.add.text(cx, cy + 308, '[ CLOSE ]', {
      fontSize: '10px',
      fontFamily: FONT_FAMILY,
      color: '#ffffff',
      backgroundColor: '#333355',
      padding: { x: 12, y: 6 },
    }).setOrigin(0.5).setDepth(201).setInteractive({ useHandCursor: true });

    closeBtn.on('pointerdown', () => this.toggleHelp());

    this.helpOverlay = [bg, text, closeBtn];
  }

  // --- Enemy Display ---

  private createEnemyDisplay(): void {
    const enemy = this.combat.enemy;
    const enemyX = LAYOUT.enemyX;
    const enemyY = LAYOUT.enemyY;
    const spriteColor = enemy.boss ? 0xc62828 : enemy.elite ? 0xffa000 : 0x4caf50;
    const spriteSize = enemy.boss ? 100 : enemy.elite ? 90 : 80;

    const enemyShadow = this.add.ellipse(enemyX, enemyY + 46, spriteSize + 30, 24, 0x020307, 0.55).setDepth(-1);
    const enemyAura = this.add.circle(enemyX, enemyY, spriteSize * 0.95, spriteColor, 0.14).setDepth(-1);
    this.tweens.add({
      targets: enemyAura,
      alpha: 0.24,
      scale: 1.08,
      duration: 1700,
      yoyo: true,
      repeat: -1,
      ease: 'Sine.easeInOut',
    });

    // Pixel-art sprite drawn via Graphics (keep enemySprite as a hidden rect for flashSprite compat)
    const pixelSprite = drawEnemySprite(this, enemyX, enemyY, enemy.name, spriteSize);
    pixelSprite.setDepth(0);
    this.tweens.add({
      targets: pixelSprite,
      scale: 1.03,
      duration: enemy.boss ? 900 : 1200,
      yoyo: true,
      repeat: -1,
      ease: 'Sine.easeInOut',
    });
    this.enemySprite = this.add.rectangle(enemyX, enemyY, spriteSize, spriteSize, spriteColor, 0)
      .setStrokeStyle(0, 0x000000, 0);

    const namePlate = this.add.rectangle(enemyX, enemyY - 63, 220, 24, 0x0f162b, 0.75).setStrokeStyle(1, 0x354b7b, 0.8);
    const nameColor = enemy.boss ? '#ff6f6f' : enemy.elite ? '#ffbf5f' : THEME.textBright;
    this.enemyNameText = this.add.text(enemyX, enemyY - 62, '', {
      fontSize: '12px',
      fontFamily: FONT_FAMILY,
      color: nameColor,
      fontStyle: 'bold',
    }).setOrigin(0.5);

    this.enemyStanceText = this.add.text(enemyX, enemyY - 40, '', {
      fontSize: '11px',
      fontFamily: FONT_FAMILY,
      color: '#ff9f68',
      fontStyle: 'bold',
      backgroundColor: '#1f2138',
      padding: { x: 6, y: 2 },
    }).setOrigin(0.5);

    if (enemy.boss || enemy.elite) {
      this.add.text(enemyX, enemyY - 82, enemy.boss ? '[ BOSS ]' : '[ ELITE ]', {
        fontSize: '12px',
        fontFamily: FONT_FAMILY,
        color: enemy.boss ? '#ff4444' : '#ffa000',
      }).setOrigin(0.5);
    }
    this.ambientObjects.push(enemyShadow, enemyAura, namePlate);

    this.enemyHealthBar = new HealthBar(this, enemyX, enemyY + 66, {
      width: ENEMY.HP_BAR_WIDTH,
      height: ENEMY.HP_BAR_HEIGHT,
      showText: true,
    });
    // Legacy references
    this.enemyHpBg = this.add.rectangle(0, 0, 1, 1, 0x000000, 0).setVisible(false);
    this.enemyHpBar = this.add.rectangle(0, 0, 1, 1, 0x000000, 0).setVisible(false);
    this.enemyHpText = this.add.text(0, 0, '', { fontSize: '1px' }).setVisible(false);

    const enemyInfoPanel = this.add.rectangle(
      LAYOUT.rightPanelX,
      LAYOUT.tacticalPanelY,
      LAYOUT.rightPanelW,
      LAYOUT.tacticalPanelH,
      THEME.panelDark,
      0.95
    )
      .setStrokeStyle(1, THEME.panelStroke, 0.86);
    const enemyInfoTitle = this.add.text(LAYOUT.rightPanelX - LAYOUT.rightPanelW / 2 + 12, LAYOUT.tacticalPanelY - LAYOUT.tacticalPanelH / 2 + 8, 'TACTICAL INFO', {
      fontSize: '10px',
      fontFamily: FONT_FAMILY,
      color: '#a9b5e5',
      fontStyle: 'bold',
    });
    this.ambientObjects.push(enemyInfoPanel, enemyInfoTitle);

    const sidebarLeft = LAYOUT.rightPanelX - LAYOUT.rightPanelW / 2 + 12;
    this.enemyIntentText = this.add.text(sidebarLeft, LAYOUT.tacticalPanelY - LAYOUT.tacticalPanelH / 2 + 24, '', {
      fontSize: '11px',
      fontFamily: FONT_FAMILY,
      color: '#ffbf74',
      align: 'left',
      wordWrap: { width: LAYOUT.rightPanelW - 24 },
      lineSpacing: 2,
    }).setOrigin(0, 0);

    this.enemyStatusText = this.add.text(sidebarLeft, LAYOUT.tacticalPanelY - LAYOUT.tacticalPanelH / 2 + 96, '', {
      fontSize: '10px',
      fontFamily: FONT_FAMILY,
      color: '#9ec9ff',
      align: 'left',
      wordWrap: { width: LAYOUT.rightPanelW - 24 },
    }).setOrigin(0, 0);

    this.mutatorText = this.add.text(sidebarLeft, LAYOUT.tacticalPanelY - LAYOUT.tacticalPanelH / 2 + 116, '', {
      fontSize: '10px',
      fontFamily: FONT_FAMILY,
      color: '#ffc18a',
      align: 'left',
      wordWrap: { width: LAYOUT.rightPanelW - 24 },
    }).setOrigin(0, 0);

    this.enemyBlockIcon = this.add.rectangle(
      enemyX + ENEMY.HP_BAR_WIDTH / 2 + 25, enemyY + 66,
      24, 24, 0x1565c0, 0.8
    ).setOrigin(0.5).setStrokeStyle(2, 0x4488cc).setVisible(false);

    this.enemyBlockText = this.add.text(
      enemyX + ENEMY.HP_BAR_WIDTH / 2 + 25, enemyY + 66, '',
      { fontSize: '12px', fontFamily: FONT_FAMILY, color: '#ffffff', fontStyle: 'bold' }
    ).setOrigin(0.5);
  }

  // --- Player HUD ---

  private createPlayerHUD(): void {
    const handRail = this.add.rectangle(LAYOUT.arenaCenterX, LAYOUT.handY + 8, LAYOUT.handRailWidth, 124, 0x10172b, 0.86)
      .setStrokeStyle(1, 0x3a5486, 0.72);
    this.ambientObjects.push(handRail);

    const hpPanel = this.add.rectangle(UI.PLAYER_HP_X + 164, UI.PLAYER_HP_Y - 4, 350, 88, 0x10182f, 0.84)
      .setStrokeStyle(1, 0x375284, 0.8);
    const deckPanel = this.add.rectangle(UI.DECK_COUNT_X - 36, UI.DECK_COUNT_Y - 4, 230, 88, 0x10182f, 0.84)
      .setStrokeStyle(1, 0x375284, 0.8);
    this.ambientObjects.push(hpPanel, deckPanel);

    this.playerHealthBar = new HealthBar(this, UI.PLAYER_HP_X + UI.HP_BAR_WIDTH / 2, UI.PLAYER_HP_Y, {
      width: UI.HP_BAR_WIDTH,
      height: UI.HP_BAR_HEIGHT,
      showText: true,
    });
    // Keep legacy references for flashSprite compatibility
    this.playerHpBg = this.add.rectangle(0, 0, 1, 1, 0x000000, 0).setVisible(false);
    this.playerHpBar = this.add.rectangle(0, 0, 1, 1, 0x000000, 0).setVisible(false);
    this.playerHpText = this.add.text(0, 0, '', { fontSize: '1px' }).setVisible(false);

    this.playerStatusText = this.add.text(UI.PLAYER_HP_X, UI.PLAYER_HP_Y + 20, '', {
      fontSize: '9px',
      fontFamily: FONT_FAMILY,
      color: THEME.accentGreen,
      wordWrap: { width: 340 },
    }).setOrigin(0, 0.5);

    this.add.text(GAME.WIDTH - 36, LAYOUT.topBarY, 'R reset  TAB loadout  H help', {
      fontSize: '10px',
      fontFamily: FONT_FAMILY,
      color: '#b8c7e8',
    }).setOrigin(1, 0.5);

    this.deckCountText = this.add.text(UI.DECK_COUNT_X, UI.DECK_COUNT_Y, '', {
      fontSize: '13px', fontFamily: FONT_FAMILY, color: '#c6ceed', align: 'right',
    }).setOrigin(1, 0.5);

    this.playerBlockIcon = this.add.rectangle(
      UI.PLAYER_HP_X + UI.HP_BAR_WIDTH + 30, UI.PLAYER_HP_Y,
      28, 28, 0x1f6fca, 0.84
    ).setOrigin(0.5).setStrokeStyle(2, 0x67a5e3).setVisible(false);

    this.playerBlockText = this.add.text(
      UI.PLAYER_HP_X + UI.HP_BAR_WIDTH + 30, UI.PLAYER_HP_Y, '',
      { fontSize: '13px', fontFamily: FONT_FAMILY, color: THEME.textBright, fontStyle: 'bold' }
    ).setOrigin(0.5);

    // Block resets label
    this.add.text(UI.PLAYER_HP_X + UI.HP_BAR_WIDTH + 55, UI.PLAYER_HP_Y, 'blk', {
      fontSize: '9px', fontFamily: FONT_FAMILY, color: THEME.accentCyan,
    }).setOrigin(0, 0.5);

    this.turnText = this.add.text(LAYOUT.arenaCenterX, LAYOUT.topBarY, '', {
      fontSize: '10px', fontFamily: FONT_FAMILY, color: '#a9b7df',
    }).setOrigin(0.5);
  }

  // --- Buttons ---

  private createEndTurnButton(): void {
    this.endTurnBtn = this.add.text(LAYOUT.arenaCenterX, LAYOUT.endTurnY, '[ END TURN ]', {
      fontSize: '12px',
      fontFamily: FONT_FAMILY,
      color: '#9aa8d8',
      backgroundColor: '#1b2444',
      padding: { x: 22, y: 10 },
    }).setOrigin(0.5).setInteractive({ useHandCursor: true });
    this.endTurnBtn.setStroke('#0a0f20', 4);

    this.endTurnBtn.on('pointerover', () => {
      if (this.combat.canEndTurn()) this.endTurnBtn.setColor('#ffd700');
    });
    this.endTurnBtn.on('pointerout', () => this.updateEndTurnButton());
    this.endTurnBtn.on('pointerdown', () => {
      if (this.combat.canEndTurn()) this.resolveEndTurn();
    });
  }

  private createResetButton(): void {
    this.resetTurnBtn = this.add.text(LAYOUT.arenaCenterX - 180, LAYOUT.endTurnY + 2, '[ RESET R ]', {
      fontSize: '14px',
      fontFamily: FONT_FAMILY,
      color: '#c2c9e8',
      backgroundColor: '#1f1f33',
      padding: { x: 12, y: 7 },
    }).setOrigin(0.5).setInteractive({ useHandCursor: true }).setDepth(6);
    this.resetTurnBtn.setStroke('#0b0d16', 3);

    this.resetTurnBtn.on('pointerover', () => {
      if (this.combat.phase === 'assign') this.resetTurnBtn.setColor('#ffe4af');
    });
    this.resetTurnBtn.on('pointerout', () => this.updateResetButton());
    this.resetTurnBtn.on('pointerdown', () => this.resetAssignments(true));
  }

  private createTurnForecastPanel(): void {
    this.turnForecastBg = this.add.rectangle(
      LAYOUT.rightPanelX,
      LAYOUT.forecastPanelY,
      LAYOUT.rightPanelW,
      LAYOUT.forecastPanelH,
      THEME.panelDark,
      0.95,
    ).setStrokeStyle(1, THEME.panelStroke, 0.86);

    const sidebarLeft = LAYOUT.rightPanelX - LAYOUT.rightPanelW / 2 + 12;
    this.add.text(sidebarLeft, LAYOUT.forecastPanelY - LAYOUT.forecastPanelH / 2 + 8, 'TURN FORECAST (EST)', {
      fontSize: '10px',
      fontFamily: FONT_FAMILY,
      color: '#ffcf8f',
      fontStyle: 'bold',
    });

    this.turnForecastText = this.add.text(sidebarLeft, LAYOUT.forecastPanelY - LAYOUT.forecastPanelH / 2 + 24, '', {
      fontSize: '10px',
      fontFamily: FONT_FAMILY,
      color: '#f5efe6',
      wordWrap: { width: LAYOUT.rightPanelW - 24 },
      lineSpacing: 3,
    });
  }

  private createFeedbackText(): void {
    this.feedbackBg = this.add.rectangle(
      LAYOUT.arenaCenterX,
      LAYOUT.feedbackY,
      560,
      42,
      0x0d1429,
      0.92,
    ).setStrokeStyle(1, 0x5870a6).setDepth(20).setAlpha(0);

    this.feedbackText = this.add.text(LAYOUT.arenaCenterX, LAYOUT.feedbackY, '', {
      fontSize: '10px',
      fontFamily: FONT_FAMILY,
      color: THEME.accentGold,
      fontStyle: 'bold',
      align: 'center',
      stroke: '#0a1020',
      strokeThickness: 4,
    }).setOrigin(0.5).setDepth(21).setAlpha(0);
  }

  private createCombatLogPanel(): void {
    this.combatLogBg = this.add.rectangle(
      LAYOUT.rightPanelX,
      LAYOUT.logPanelY,
      LAYOUT.rightPanelW,
      LAYOUT.logPanelH,
      THEME.panelDark,
      0.95,
    ).setStrokeStyle(1, THEME.panelStroke, 0.86);

    const sidebarLeft = LAYOUT.rightPanelX - LAYOUT.rightPanelW / 2 + 12;
    this.add.text(sidebarLeft, LAYOUT.logPanelY - LAYOUT.logPanelH / 2 + 8, 'COMBAT LOG', {
      fontSize: '10px',
      fontFamily: FONT_FAMILY,
      color: THEME.textMuted,
      fontStyle: 'bold',
    });

    this.combatLogText = this.add.text(sidebarLeft, LAYOUT.logPanelY - LAYOUT.logPanelH / 2 + 24, '', {
      fontSize: '10px',
      fontFamily: FONT_FAMILY,
      color: THEME.textBright,
      wordWrap: { width: LAYOUT.rightPanelW - 24 },
      lineSpacing: 2,
    });
  }

  // --- Turn Logic ---

  private startTurn(): void {
    for (const cs of this.cardSprites) cs.container.destroy();
    this.cardSprites = [];
    for (const zone of this.zones) zone.cards = [];

    const drawn = this.combat.startTurn();
    this.createCardSprites(drawn);
    this.updateAllUI();
  }

  private createCardSprites(cards: CardData[]): void {
    const spacing = Math.min(HAND.SPACING, Math.floor((LAYOUT.handRailWidth - CARD.WIDTH) / Math.max(1, cards.length - 1)));
    const startX = LAYOUT.arenaCenterX - ((cards.length - 1) * spacing) / 2;
    const mid = (cards.length - 1) / 2;

    for (let i = 0; i < cards.length; i++) {
      const card = cards[i];
      const x = startX + i * spacing;
      const spread = Math.abs(i - mid);
      const y = LAYOUT.handY + spread * 2;
      const rotation = (i - mid) * 0.02;

      const shadow = this.add.rectangle(4, 7, CARD.WIDTH + 2, CARD.HEIGHT + 2, 0x000000, 0.38);
      const bg = this.add.rectangle(0, 0, CARD.WIDTH, CARD.HEIGHT, CARD.COLORS.hand);
      const innerFrame = this.add.rectangle(0, 0, CARD.WIDTH - 10, CARD.HEIGHT - 10, 0x1f2340, 0.34)
        .setStrokeStyle(1, 0x5d6691, 0.5);
      const gloss = this.add.rectangle(0, -CARD.HEIGHT / 2 + 16, CARD.WIDTH - 8, 18, 0xffffff, 0.09);
      const rarityColor = this.getCardRarityBorder(card);
      bg.setStrokeStyle(2, rarityColor);

      const numColor = card.kind === 'curse'
        ? '#ff8899'
        : card.number < 0 ? '#ff6666' : CARD.COLORS.number;
      const numLabel = card.kind === 'curse' ? '0' : `${card.number}`;
      const numText = this.add.text(0, -8, numLabel, {
        fontSize: `${CARD.FONT_SIZE}px`,
        fontFamily: FONT_FAMILY,
        color: numColor,
        fontStyle: 'bold',
      }).setOrigin(0.5);

      const children: Phaser.GameObjects.GameObject[] = [shadow, bg, innerFrame, gloss, numText];
      let modTagText: Phaser.GameObjects.Text | undefined;
      let modStateText: Phaser.GameObjects.Text | undefined;

      // Negative indicator
      if (card.kind === 'curse') {
        const curseLabel = this.add.text(0, -42, 'CURSE', {
          fontSize: '8px', fontFamily: FONT_FAMILY, color: '#ff8899',
          fontStyle: 'bold',
        }).setOrigin(0.5);
        children.push(curseLabel);
      } else if (card.number < 0) {
        const negLabel = this.add.text(0, -42, 'NEG', {
          fontSize: '8px', fontFamily: FONT_FAMILY, color: '#ff4444',
        }).setOrigin(0.5);
        children.push(negLabel);
      }

      // Modifier indicator
      if (card.modifiers.length > 0) {
        const hasTempMod = card.modifiers.some(m => m.temporary);
        const dotColor = hasTempMod ? 0xffd700 : 0x66cc88;
        const dot = this.add.circle(0, 30, 5, dotColor).setStrokeStyle(1, 0x0d1020, 0.9);
        modTagText = this.add.text(0, 42, '', {
          fontSize: '9px', fontFamily: FONT_FAMILY, color: '#ffd166',
        }).setOrigin(0.5);
        modStateText = this.add.text(0, 52, '', {
          fontSize: '8px', fontFamily: FONT_FAMILY, color: '#99ccaa',
        }).setOrigin(0.5);
        children.push(dot, modTagText, modStateText);
      }

      const container = this.add.container(x, y, children);
      container.setSize(CARD.WIDTH, CARD.HEIGHT);
      container.setRotation(rotation);
      container.setInteractive({ draggable: true, useHandCursor: true });
      this.input.setDraggable(container);

      container.on('pointerover', () => {
        bg.setStrokeStyle(3, CARD.COLORS.hover);
        container.setScale(cardSprite.baseScale + 0.12);
        container.setDepth(10);
        this.showTooltip(card, container.x, container.y);
      });
      container.on('pointerout', () => {
        container.setScale(cardSprite.baseScale);
        container.setDepth(0);
        this.hideTooltip();
        this.updateCardModifierIndicator(cardSprite);
      });

      const cardSprite: CardSprite = {
        container,
        bg,
        numText,
        modTagText,
        modStateText,
        data: card,
        originalX: x,
        originalY: y,
        originalRotation: rotation,
        baseScale: 1,
      };
      this.cardSprites.push(cardSprite);
      this.updateCardModifierIndicator(cardSprite);

      container.y += 26;
      container.alpha = 0;
      this.tweens.add({
        targets: container,
        y,
        alpha: 1,
        duration: 180 + i * 30,
        ease: 'Back.easeOut',
      });
    }
  }

  private getCardRarityBorder(card: CardData): number {
    if (card.kind === 'curse') return 0xaa3355;
    const hasRare = card.modifiers.some(mod => mod.rarity === 'rare');
    if (hasRare) return 0xf7d15b;
    const hasUncommon = card.modifiers.some(mod => mod.rarity === 'uncommon');
    if (hasUncommon) return 0x63b4ff;
    const hasAny = card.modifiers.length > 0;
    if (hasAny) return 0x7fc48a;
    return CARD.COLORS.border;
  }

  private getSlotBorderColor(slot: CardSlot): number {
    switch (slot) {
      case 'attack': return CARD.COLORS.attack;
      case 'defense': return CARD.COLORS.defense;
      case 'ability': return CARD.COLORS.ability;
      default: return CARD.COLORS.border;
    }
  }

  // --- Card Drag & Drop ---

  private handleCardDrop(cardSprite: CardSprite): void {
    const { container, data } = cardSprite;
    const hoveredZone = this.getZoneAtPosition(container.x, container.y);
    let assignedToZone = false;

    if (hoveredZone) {
      this.removeCardFromZones(cardSprite);
      const zoneCap = this.combat.getZoneCap(hoveredZone.slot);
      if (hoveredZone.cards.length >= zoneCap) {
        data.slot = 'hand';
        cardSprite.bg.setFillStyle(CARD.COLORS.hand);
        cardSprite.baseScale = 1;
        this.updateCardModifierIndicator(cardSprite);
        this.tweens.add({
          targets: container,
          x: cardSprite.originalX, y: cardSprite.originalY, scale: 1, rotation: cardSprite.originalRotation,
          duration: 150, ease: 'Back.easeOut',
        });
        this.tweens.add({
          targets: hoveredZone.container,
          x: hoveredZone.container.x + 6,
          duration: 70,
          yoyo: true,
          repeat: 1,
          ease: 'Sine.easeInOut',
        });
        this.showFeedback(`Zone full (${hoveredZone.cards.length}/${zoneCap})`, THEME.accentRed);
        this.updateAllUI();
        return;
      }

      data.slot = hoveredZone.slot;
      hoveredZone.cards.push(cardSprite);
      assignedToZone = true;
      cardSprite.bg.setFillStyle(this.getSlotFillColor(hoveredZone.slot));
      this.updateCardModifierIndicator(cardSprite);
      this.layoutZoneCards(hoveredZone);
    }

    if (!assignedToZone) {
      this.removeCardFromZones(cardSprite);
      data.slot = 'hand';
      cardSprite.bg.setFillStyle(CARD.COLORS.hand);
      cardSprite.baseScale = 1;
      this.updateCardModifierIndicator(cardSprite);
      this.tweens.add({
        targets: container,
        x: cardSprite.originalX, y: cardSprite.originalY, scale: 1, rotation: cardSprite.originalRotation,
        duration: 150, ease: 'Back.easeOut',
      });
    }

    this.updateAllUI();
  }

  private getSlotFillColor(slot: CardSlot): number {
    switch (slot) {
      case 'attack': return 0x4a1111;
      case 'defense': return 0x0d2744;
      case 'ability': return 0x1a3a1a;
      default: return CARD.COLORS.hand;
    }
  }

  private removeCardFromZones(cardSprite: CardSprite): void {
    for (const zone of this.zones) {
      const idx = zone.cards.indexOf(cardSprite);
      if (idx !== -1) {
        zone.cards.splice(idx, 1);
        this.layoutZoneCards(zone);
        break;
      }
    }
  }

  private layoutZoneCards(zone: ZoneSprite): void {
    if (zone.cards.length === 0) return;
    const count = zone.cards.length;
    const spacing = Math.min(58, (ZONES.WIDTH - CARD.WIDTH - 24) / Math.max(1, count - 1));
    const startX = zone.container.x - ((count - 1) * spacing) / 2;
    const targetY = zone.container.y + 52;
    const targetScale = count >= 4 ? 0.64 : count === 3 ? 0.7 : count === 2 ? 0.78 : 0.86;

    for (let i = 0; i < count; i++) {
      const cardSprite = zone.cards[i];
      cardSprite.baseScale = targetScale;
      this.tweens.add({
        targets: cardSprite.container,
        x: startX + i * spacing,
        y: targetY,
        scale: targetScale,
        rotation: 0,
        duration: 150,
        ease: 'Power2',
      });
    }
  }

  private resetAssignments(showFeedback: boolean): void {
    if (this.combat.phase !== 'assign') return;

    let moved = 0;
    for (const zone of this.zones) {
      moved += zone.cards.length;
      zone.cards = [];
    }

    for (const cardSprite of this.cardSprites) {
      if (cardSprite.data.slot === 'hand') continue;
      cardSprite.data.slot = 'hand';
      cardSprite.bg.setFillStyle(CARD.COLORS.hand);
      cardSprite.baseScale = 1;
      this.updateCardModifierIndicator(cardSprite);
      this.tweens.add({
        targets: cardSprite.container,
        x: cardSprite.originalX,
        y: cardSprite.originalY,
        scale: 1,
        rotation: cardSprite.originalRotation,
        duration: 160,
        ease: 'Back.easeOut',
      });
    }

    this.resetDropZoneHighlight();
    this.updateAllUI();
    if (showFeedback && moved > 0) {
      this.showFeedback('Assignments cleared', THEME.accentCyan);
    }
  }

  // --- Turn Resolution ---

  private async resolveEndTurn(): Promise<void> {
    if (this.loadoutOverlay.length > 0) {
      this.toggleLoadoutOverlay();
    }
    this.endTurnBtn.disableInteractive();
    for (const cs of this.cardSprites) {
      cs.container.disableInteractive();
      this.input.setDraggable(cs.container, false);
    }
    this.hideTooltip();
    this.comboPreviewText.setText('');

    const result = this.combat.endTurn();
    if (!result) return;

    this.run.combosTriggered += result.combos.length;

    await this.animateResolution(result);

    if (this.combat.isOver) {
      this.run.playerHp = Math.max(0, this.combat.player.hp);
      this.run.permanentExhaust = [...this.combat.player.permanentExhaust];

      this.time.delayedCall(800, () => {
        if (this.combat.phase === 'victory') {
          this.run.battlesWon++;
          if (this.nodeType === 'elite') this.run.elitesDefeated++;
          if (this.nodeType === 'boss') this.run.bossesDefeated++;
          if (this.run.drawBonusBattles > 0) this.run.drawBonusBattles--;

          // Post-combat relic healing
          if (hasRelic(this.run, 'healing_herb')) {
            this.run.playerHp = Math.min(this.run.playerMaxHp, this.run.playerHp + 3);
          }
          if (hasRelic(this.run, 'burning_blood')) {
            this.run.playerHp = Math.min(this.run.playerMaxHp, this.run.playerHp + 6);
          }

          const rewards = generateVictoryRewards({
            battleNumber: this.run.battlesWon,
            nodeType: this.nodeType,
            existingDeck: this.run.deckNumbers,
            ownedAbilityIds: this.run.abilities.map(a => a.id),
            unlockSnapshot: this.run.unlockSnapshot,
          });
          fadeOut(this).then(() => this.scene.start('RewardScene', { rewards, run: this.run, nodeType: this.nodeType }));
        } else {
          if (this.run.drawBonusBattles > 0) this.run.drawBonusBattles--;
          fadeOut(this).then(() => this.scene.start('GameOverScene', {
            victory: false,
            turnsPlayed: this.run.battlesWon,
            playerHp: 0,
            maxHp: this.run.playerMaxHp,
            isRunComplete: false,
            combos: this.run.combosTriggered,
            elites: this.run.elitesDefeated,
            bosses: this.run.bossesDefeated,
            run: this.run,
          }));
        }
      });
      return;
    }

    this.time.delayedCall(600, () => this.startTurn());
  }

  private async animateResolution(result: CombatResult): Promise<void> {
    // Combos
    for (const combo of result.combos) {
      this.showFeedback(`${combo.name}: ${combo.description}`, '#ffd700');
      showComboPopup(this, LAYOUT.arenaCenterX, LAYOUT.comboY - 30, combo.name);
      emitComboParticles(this, LAYOUT.arenaCenterX, LAYOUT.comboY);
      await this.delay(700);
    }

    // Abilities
    for (const effect of result.abilityEffects) {
      this.showFeedback(effect.description, '#2e7d32');
      await this.delay(400);
    }

    // Defense
    if (result.playerBlock > 0) {
      this.showFeedback(`Block: ${result.playerBlock}`, '#1565c0');
      emitBlockParticles(this, UI.PLAYER_HP_X + UI.HP_BAR_WIDTH / 2, UI.PLAYER_HP_Y);
      this.updateAllUI();
      await this.delay(300);
    }

    // Player attack
    if (result.enemyDamage > 0) {
      this.showFeedback(`You deal ${result.enemyDamage} damage!`, '#ffd700');
      showFloatingNumber(this, LAYOUT.enemyX, LAYOUT.enemyY - 20, result.enemyDamage, 'damage');
      emitDamageParticles(this, LAYOUT.enemyX, LAYOUT.enemyY, result.enemyDamage);
      shakeLight(this);
      this.updateAllUI();
      await this.delay(400);
    } else if (result.enemyDamage < 0) {
      this.showFeedback(`Enemy heals ${-result.enemyDamage}!`, '#ff6666');
      this.updateAllUI();
      await this.delay(400);
    }

    // Enemy attack
    if (result.playerDamage > 0) {
      this.showFeedback(`Enemy deals ${result.playerDamage} damage!`, '#c62828');
      showFloatingNumber(this, UI.PLAYER_HP_X + UI.HP_BAR_WIDTH / 2, UI.PLAYER_HP_Y - 20, result.playerDamage, 'damage');
      emitDamageParticles(this, UI.PLAYER_HP_X + UI.HP_BAR_WIDTH / 2, UI.PLAYER_HP_Y, result.playerDamage);
      this.shakeCamera();
      this.updateAllUI();
      await this.delay(400);
    }

    // Heal
    if (result.playerHeal > 0) {
      this.showFeedback(`Healed ${result.playerHeal} HP`, '#66cc66');
      showFloatingNumber(this, UI.PLAYER_HP_X + UI.HP_BAR_WIDTH / 2, UI.PLAYER_HP_Y - 20, result.playerHeal, 'heal');
      emitHealParticles(this, UI.PLAYER_HP_X + UI.HP_BAR_WIDTH / 2, UI.PLAYER_HP_Y);
      this.updateAllUI();
      await this.delay(300);
    }

    this.updateAllUI();
  }

  private showFeedback(text: string, color: string): void {
    this.feedbackText.setText(text);
    this.feedbackText.setColor(color);
    const bounds = this.feedbackText.getBounds();
    this.feedbackBg.setSize(Math.max(320, bounds.width + 34), 48);
    this.feedbackText.setAlpha(1);
    this.feedbackBg.setAlpha(1);
    this.feedbackText.y = LAYOUT.feedbackY;
    this.feedbackBg.y = LAYOUT.feedbackY;
    this.tweens.add({
      targets: this.feedbackText,
      alpha: 0, y: LAYOUT.feedbackY - 34,
      duration: 1300, ease: 'Power2',
    });
    this.tweens.add({
      targets: this.feedbackBg,
      alpha: 0, y: LAYOUT.feedbackY - 34,
      duration: 1300, ease: 'Power2',
    });
  }

  private shakeCamera(): void {
    if (this.nodeType === 'boss') {
      shakeHeavy(this);
    } else {
      this.cameras.main.shake(ANIM.SHAKE_DURATION, ANIM.SHAKE_INTENSITY / 1000);
    }
  }

  private flashSprite(sprite: Phaser.GameObjects.Rectangle, color: number): void {
    const originalColor = sprite.fillColor;
    sprite.setFillStyle(color);
    this.time.delayedCall(ANIM.DAMAGE_FLASH_DURATION, () => sprite.setFillStyle(originalColor));
  }

  private delay(ms: number): Promise<void> {
    return new Promise(resolve => this.time.delayedCall(ms, resolve));
  }

  private formatStatuses(statuses?: StatusState[]): string {
    if (!statuses || statuses.length === 0) return '—';
    return statuses
      .map(status => `${status.type.toUpperCase()} x${status.stacks}`)
      .join('  ');
  }

  private formatMutators(enemy: EnemyData): string {
    if (!enemy.mutators || enemy.mutators.length === 0) return 'none';
    return enemy.mutators.map(m => m.toUpperCase()).join('  ');
  }

  private getStanceLabel(enemy: EnemyData): string {
    const stance = enemy.stance ?? 'aggro';
    if (stance === 'aggro') return 'AGGRO';
    if (stance === 'guard') return 'GUARD';
    return 'CAST';
  }

  private getStanceColor(enemy: EnemyData): string {
    const stance = enemy.stance ?? 'aggro';
    if (stance === 'aggro') return '#ff8a80';
    if (stance === 'guard') return '#8ab4ff';
    return '#d7a5ff';
  }

  private describeIntent(intent: { type: string; value: number; description?: string }): string {
    switch (intent.type) {
      case 'attack': return `ATK ${intent.value}`;
      case 'defend': return `DEF ${intent.value}`;
      case 'buff': return intent.description ?? 'BUFF';
      case 'summon': return intent.description ?? 'SUMMON';
      default: return intent.description ?? intent.type.toUpperCase();
    }
  }

  private trimLine(line: string, maxChars: number): string {
    if (line.length <= maxChars) return line;
    return `${line.slice(0, Math.max(0, maxChars - 3))}...`;
  }

  private renderTargetPanel(): void {
    for (const obj of this.targetPanelObjects) {
      obj.destroy();
    }
    this.targetPanelObjects = [];

    const adds = this.combat.getAdds().filter(enemy => enemy.hp > 0);
    if (adds.length === 0) return;

    const enemies = [this.combat.enemy, ...adds];
    const selectedTargetId = this.combat.getActiveTargetId();
    const sidebarLeft = LAYOUT.rightPanelX - LAYOUT.rightPanelW / 2 + 12;
    const rowStartY = LAYOUT.targetPanelY - LAYOUT.targetPanelH / 2 + 24;

    const panel = this.add.rectangle(LAYOUT.rightPanelX, LAYOUT.targetPanelY, LAYOUT.rightPanelW, LAYOUT.targetPanelH, 0x0f1630, 0.92)
      .setStrokeStyle(1, 0x3f4f84, 0.82);
    const title = this.add.text(sidebarLeft, LAYOUT.targetPanelY - LAYOUT.targetPanelH / 2 + 8, 'TARGET SELECT', {
      fontSize: '10px',
      fontFamily: FONT_FAMILY,
      color: '#9fb6ff',
      fontStyle: 'bold',
    });
    this.targetPanelObjects.push(panel, title);

    for (let i = 0; i < enemies.length; i++) {
      const foe = enemies[i];
      const y = rowStartY + i * 24;
      const isSelected = foe.id === selectedTargetId;
      const stroke = isSelected ? 0xffd166 : foe.id === this.combat.enemy.id ? 0xff8f8f : 0x7fa1c6;
      const fill = foe.id === this.combat.enemy.id ? 0x331d1d : 0x172539;

      const row = this.add.rectangle(LAYOUT.rightPanelX, y + 8, LAYOUT.rightPanelW - 16, 18, fill, 0.92)
        .setStrokeStyle(isSelected ? 2 : 1, stroke);
      const foeLabel = this.trimLine(`${foe.name} ${Math.max(0, foe.hp)}/${foe.maxHp}`, 22);
      const label = this.add.text(sidebarLeft + 4, y + 2, foeLabel, {
        fontSize: '9px',
        fontFamily: FONT_FAMILY,
        color: '#e8ebff',
      });
      const stance = this.add.text(LAYOUT.rightPanelX + LAYOUT.rightPanelW / 2 - 64, y + 2, this.getStanceLabel(foe), {
        fontSize: '8px',
        fontFamily: FONT_FAMILY,
        color: this.getStanceColor(foe),
      });
      const targetHint = this.add.text(LAYOUT.rightPanelX + LAYOUT.rightPanelW / 2 - 20, y + 2, isSelected ? 'ACTIVE' : 'select', {
        fontSize: '8px',
        fontFamily: FONT_FAMILY,
        color: isSelected ? '#ffd166' : '#9fb6ff',
      });

      row.setInteractive({ useHandCursor: true });
      row.on('pointerdown', () => {
        this.combat.setActiveTarget(foe.id);
        this.updateAllUI();
      });

      this.targetPanelObjects.push(row, label, stance, targetHint);
    }
  }

  // --- UI Update ---

  private updateAllUI(): void {
    // Player HP
    this.playerHealthBar.update(
      Math.max(0, this.combat.player.hp),
      this.combat.player.maxHp,
    );

    // Player block
    const playerBlock = this.combat.player.block;
    if (playerBlock > 0) {
      this.playerBlockIcon.setVisible(true);
      this.playerBlockText.setText(`${playerBlock}`);
    } else {
      this.playerBlockIcon.setVisible(false);
      this.playerBlockText.setText('');
    }

    // Deck
    const draw = this.combat.deck.remainingCards;
    const discard = this.combat.deck.discardPile.length;
    const drawPreview = this.combat.getNextDrawPreview();
    const previewParts: string[] = [];
    if (drawPreview.bonus > 0) previewParts.push(`+${drawPreview.bonus}`);
    if (drawPreview.penalty > 0) previewParts.push(`-${drawPreview.penalty}`);
    const previewText = previewParts.length > 0 ? `\nNext draw ${previewParts.join(' ')}` : '\nNext draw —';
    this.deckCountText.setText(`Draw ${draw}  Discard ${discard}${previewText}`);
    this.deckCountText.setColor('#d6deff');
    const playerStatuses = this.formatStatuses(this.combat.player.statuses);
    const playerStatusLine = playerStatuses === '—'
      ? 'Status none'
      : this.trimLine(`Status ${playerStatuses}`, 52);
    this.playerStatusText.setText(playerStatusLine);
    this.playerStatusText.setColor(playerStatuses === '—' ? THEME.textMuted : THEME.accentGreen);

    // Enemy
    this.enemyNameText.setText(this.combat.enemy.name);
    this.enemyStanceText.setText(`[ ${this.getStanceLabel(this.combat.enemy)} ]`);
    this.enemyStanceText.setColor(this.getStanceColor(this.combat.enemy));
    this.enemyHealthBar.update(
      Math.max(0, this.combat.enemy.hp),
      this.combat.enemy.maxHp,
    );
    const enemyStatuses = this.formatStatuses(this.combat.enemy.statuses);
    const enemyStatusLine = enemyStatuses === '—' ? 'Status none' : this.trimLine(`Status ${enemyStatuses}`, 34);
    this.enemyStatusText.setText(enemyStatusLine);
    this.enemyStatusText.setColor(enemyStatuses === '—' ? THEME.textMuted : '#9ec9ff');
    const mutators = this.formatMutators(this.combat.enemy);
    const mutatorLine = mutators === 'none' ? 'Mutator none' : this.trimLine(`Mutator ${mutators}`, 34);
    this.mutatorText.setText(mutatorLine);
    this.mutatorText.setColor(mutators === 'none' ? THEME.textMuted : '#ffc18a');

    // Enemy block
    const enemyBlock = this.combat.enemy.block;
    if (enemyBlock > 0) {
      this.enemyBlockIcon.setVisible(true);
      this.enemyBlockText.setText(`${enemyBlock}`);
    } else {
      this.enemyBlockIcon.setVisible(false);
      this.enemyBlockText.setText('');
    }

    // Enemy intent panel
    const intentViews = this.combat.getEnemyIntentViews();
    const lineLimit = Math.max(34, Math.floor((LAYOUT.rightPanelW - 24) / 5.2));
    const intentLines = intentViews.map(view => {
      const role = view.isPrimary ? 'MAIN' : 'ADD';
      const line = `${role} ${view.enemyName}: ${this.describeIntent(view.intent)}`;
      return this.trimLine(line, lineLimit);
    });
    this.enemyIntentText.setText(intentLines.join('\n'));
    this.enemyIntentText.setColor('#ffbf74');

    const logLines = this.combat.getCombatLog();
    const trimmed = logLines.map(line => this.trimLine(line, lineLimit));
    this.combatLogText.setText(trimmed.length > 0 ? trimmed.join('\n') : 'No events yet');

    // Turn info
    const nodeLabel = this.nodeType === 'boss' ? 'BOSS' : this.nodeType === 'elite' ? 'ELITE' : 'Battle';
    const caps = this.combat.getZoneCaps();
    this.turnText.setText(`${nodeLabel}  |  Turn ${this.combat.turnNumber}  |  Caps A${caps.attack}/D${caps.defense}/L${caps.ability}`);

    // Zone display
    for (const zone of this.zones) {
      const zoneCap = this.combat.getZoneCap(zone.slot);
      const zoneLabel = zone.slot === 'attack' ? 'ATK'
        : zone.slot === 'defense' ? 'DEF' : 'ABL';
      zone.label.setText(`${zoneLabel} ${zone.cards.length}/${zoneCap}`);
      zone.descText.setColor(this.activeDropZone === zone ? '#f6f8ff' : THEME.textMuted);
      zone.countText.setColor(this.activeDropZone === zone ? '#ffffff' : '#ecf2ff');

      if (zone.cards.length > 0) {
        if (zone.slot === 'attack') {
          const projected = this.getProjectedAttack(zone.cards.map(cs => cs.data));
          zone.countText.setText(projected >= 0 ? `Projected DMG ${projected}` : `Enemy Heal ${-projected}`);
        } else if (zone.slot === 'defense') {
          const projected = this.getProjectedDefense(zone.cards.map(cs => cs.data));
          zone.countText.setText(projected >= 0 ? `Projected BLK ${projected}` : `Block Loss ${-projected}`);
        } else {
          const { active, inactive } = this.getAbilityModifierState(zone.cards.map(cs => cs.data));
          zone.countText.setText(`Modifiers ON ${active} / OFF ${inactive}`);
        }
      } else {
        zone.countText.setText(this.activeDropZone === zone ? 'Drop card here' : 'Zone empty');
      }
    }

    // Ability warning
    const abilityZone = this.zones.find(z => z.slot === 'ability');
    if (abilityZone && this.abilityWarning) {
      if (abilityZone.cards.length > 0) {
        const cards = abilityZone.cards.map(cs => cs.data);
        const allMods = cards.flatMap(c => c.modifiers);
        const matching = cards.reduce((sum, card) => sum + card.modifiers.filter(mod => mod.trigger === 'ability').length, 0);
        if (allMods.length === 0) {
          this.abilityWarning.setColor('#aa8844');
          this.abilityWarning.setText('Base cards in ABL have no direct effect');
        } else if (matching === 0) {
          const needed = Array.from(new Set(cards
            .flatMap(card => this.getCardModifierTargets(card))
            .map(slot => this.getTriggerLabel(slot))))
            .join('/');
          this.abilityWarning.setColor('#ff6666');
          this.abilityWarning.setText(`Mods inactive here. Needed: ${needed}`);
        } else {
          this.abilityWarning.setColor('#66cc66');
          this.abilityWarning.setText('Ability modifiers active');
        }
      } else {
        this.abilityWarning.setText('');
      }
    }

    // Combo preview
    this.updateComboPreview();
    this.renderTargetPanel();
    this.updateTurnForecast();

    this.updateEndTurnButton();
    this.updateResetButton();
  }

  private getProjectedAttack(cards: CardData[]): number {
    let total = 0;
    for (const card of cards) {
      if (card.kind === 'number') {
        total += card.number;
      }
      for (const mod of card.modifiers) {
        if (mod.trigger !== 'attack') continue;
        if (mod.effect.type === 'extra_damage' || mod.effect.type === 'buff_attack') {
          total += mod.effect.value;
        }
      }
    }
    return total;
  }

  private getProjectedDefense(cards: CardData[]): number {
    let total = 0;
    for (const card of cards) {
      if (card.kind === 'number') {
        total += card.number;
      }
      for (const mod of card.modifiers) {
        if (mod.trigger !== 'defense') continue;
        if (mod.effect.type === 'extra_block') {
          total += mod.effect.value;
        }
      }
    }
    return total;
  }

  private getAbilityModifierState(cards: CardData[]): { active: number; inactive: number } {
    let active = 0;
    let inactive = 0;
    for (const card of cards) {
      for (const mod of card.modifiers) {
        if (mod.trigger === 'ability') active += 1;
        else inactive += 1;
      }
    }
    return { active, inactive };
  }

  private getForecastTargetEnemy(): EnemyData {
    const targetId = this.combat.getActiveTargetId();
    if (targetId === this.combat.enemy.id) return this.combat.enemy;
    const add = this.combat.getAdds().find(enemy => enemy.id === targetId && enemy.hp > 0);
    return add ?? this.combat.enemy;
  }

  private getEstimatedIncomingDamage(projectedBlock: number): { incomingRaw: number; incomingAfterBlock: number } {
    const intentViews = this.combat.getEnemyIntentViews();
    const incomingRaw = intentViews.reduce((sum, view) => {
      if (view.intent.type !== 'attack') return sum;
      return sum + Math.max(0, view.intent.value);
    }, 0);
    const incomingAfterBlock = Math.max(0, incomingRaw - projectedBlock);
    return { incomingRaw, incomingAfterBlock };
  }

  private updateTurnForecast(): void {
    if (this.combat.phase !== 'assign') {
      this.turnForecastText.setText('Forecast unavailable\noutside assign phase');
      return;
    }

    const attackCards = this.zones.find(z => z.slot === 'attack')?.cards.map(cs => cs.data) ?? [];
    const defenseCards = this.zones.find(z => z.slot === 'defense')?.cards.map(cs => cs.data) ?? [];
    const abilityCards = this.zones.find(z => z.slot === 'ability')?.cards.map(cs => cs.data) ?? [];
    const assigned = attackCards.length + defenseCards.length + abilityCards.length;

    if (assigned === 0) {
      this.turnForecastText.setText('Assign cards to preview:\nOutgoing / Incoming / HP delta');
      return;
    }

    const targetEnemy = this.getForecastTargetEnemy();
    const projectedAttack = this.getProjectedAttack(attackCards);
    const projectedDefense = this.getProjectedDefense(defenseCards);
    const projectedBlock = Math.max(0, this.combat.player.block + projectedDefense);
    const { incomingRaw, incomingAfterBlock } = this.getEstimatedIncomingDamage(projectedBlock);
    const nextPlayerHp = Math.max(0, this.combat.player.hp - incomingAfterBlock);

    let enemyDeltaText = 'Out: no direct attack';
    let nextEnemyHp = targetEnemy.hp;
    if (projectedAttack > 0) {
      const estimatedDamage = Math.max(0, projectedAttack - targetEnemy.block);
      nextEnemyHp = Math.max(0, targetEnemy.hp - estimatedDamage);
      enemyDeltaText = `Out: ~${estimatedDamage} dmg to ${targetEnemy.name} (blk ${targetEnemy.block})`;
    } else if (projectedAttack < 0) {
      const enemyHeal = -projectedAttack;
      nextEnemyHp = Math.min(targetEnemy.maxHp, targetEnemy.hp + enemyHeal);
      enemyDeltaText = `Out: heal ${targetEnemy.name} by ${enemyHeal}`;
    }

    const abilityState = this.getAbilityModifierState(abilityCards);
    const lines = [
      enemyDeltaText,
      `In: ${incomingRaw} atk vs blk ${projectedBlock} -> ${incomingAfterBlock} hp`,
      `HP next: You ${nextPlayerHp} | ${targetEnemy.name} ${nextEnemyHp}/${targetEnemy.maxHp}`,
      `ABL mods: ${abilityState.active} on / ${abilityState.inactive} off`,
    ];
    this.turnForecastText.setText(lines.join('\n'));
  }

  private updateEndTurnButton(): void {
    if (this.combat.canEndTurn()) {
      this.endTurnBtn.setText('[ END TURN  SPACE ]');
      this.endTurnBtn.setColor('#ffffff');
      this.endTurnBtn.setBackgroundColor('#2b3f6f');
      this.endTurnBtn.setInteractive({ useHandCursor: true });
    } else {
      this.endTurnBtn.setText('[ PLACE AT LEAST 1 CARD ]');
      this.endTurnBtn.setColor('#7885af');
      this.endTurnBtn.setBackgroundColor('#1a2140');
    }
  }

  private updateResetButton(): void {
    const hasAssignedCards = this.zones.some(zone => zone.cards.length > 0);
    const canReset = this.combat.phase === 'assign' && hasAssignedCards;
    if (canReset) {
      this.resetTurnBtn.setColor('#f6ecd3');
      this.resetTurnBtn.setBackgroundColor('#3d3043');
      this.resetTurnBtn.setInteractive({ useHandCursor: true });
    } else {
      this.resetTurnBtn.setColor('#7f7f95');
      this.resetTurnBtn.setBackgroundColor('#222334');
      this.resetTurnBtn.disableInteractive();
    }
  }
}
